// Copyright (c) StrangeBee 2024

import { Alert, Layout } from 'antd';
import React, { ReactElement, ReactNode, useContext } from 'react';
import { UserOutput, BrandingParams, LicenseOutput } from 'thehive-sdk';
import ThHeader from '@Organisms/MainLayout/Header/ThHeader';
import MainLayoutSider from '@Organisms/MainLayout/MainLayoutSider/MainLayoutSider';
import { OrganisationSwitcher } from '@Organisms/MainLayout/OrganisationSwitcher/OrganisationSwitcher';
import { UserEventList } from '@Organisms/User/eventList';
import { openMessage } from '@Helpers/notification';
import { useLocalMessage } from '@Helpers/locale';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { LicenseTrial } from '@Organisms/License/LicenseTrial/LicenseTrial';
import { StatusContext } from '@Context/StatusProvider';
import { ICONS } from '@Constants/iconsApp';
import { useHistory } from 'react-router-dom';
import { useStorageOptions } from '@Hooks/useStorageOptions';

import './MainLayout.scss';

const { Content } = Layout;

interface MainLayoutProps {
    menu?: ReactElement;
    children: ReactNode;
    userData?: UserOutput;
    brandingData?: BrandingParams;
    userEventList: UserEventList;
}

const MainLayout = ({ menu, children, userData, userEventList, brandingData }: MainLayoutProps): ReactElement => {
    const i = useLocalMessage();
    const { validLicense = true } = useContext(StatusContext);
    const history = useHistory();

    const { homepage } = useStorageOptions();

    const { data: licenseDetails } = useGenericQuery<LicenseOutput>({
        fetchCallback: () => client.LicenseController.get<LicenseOutput>(),
        key: ['license'],
        manual: true,
        fetchOnStart: true,
    });

    const { organisation = '', organisations = [] } = userData || {};

    const { license } = licenseDetails || {};

    return (
        <div className="main-layout-container">
            <Layout className="main-layout">
                {/* HEADER */}
                {validLicense ? (
                    license && (
                        <LicenseTrial expirationDate={license.expiresAt} plan={license.plan} kind={license.kind} />
                    )
                ) : (
                    <Alert
                        className="license-error"
                        type="error"
                        message={
                            <span className="license-error__description">
                                <ICONS.WarningOutlined />
                                <b>{i('license.error-message')}</b>
                            </span>
                        }
                    />
                )}
                <ThHeader brandingData={brandingData} userData={userData} />
                {/* Global Layout */}
                <Layout className="main-layout__wrapper">
                    {organisations.length > 1 && (
                        <div className="main-layout__organisations">
                            <OrganisationSwitcher
                                selectedOrg={organisation}
                                userOrganisations={organisations}
                                onSwitch={(orgId) =>
                                    userEventList.onSwitchOrganisation(orgId).then(() => {
                                        openMessage('success', i('notification.organisation.switch'));

                                        const orgAdmin = organisations?.find(
                                            ({ organisation, organisationId }) =>
                                                organisation === 'admin' && orgId === organisationId,
                                        );

                                        if (orgAdmin?.organisation === 'admin' || organisation === 'admin') {
                                            window.location.reload();
                                            return;
                                        }

                                        history.push(homepage);
                                        window.location.reload();
                                    })
                                }
                            />
                        </div>
                    )}
                    <MainLayoutSider menu={menu} />
                    <Content className="main-layout-wrapper__content">{children}</Content>
                </Layout>
            </Layout>
        </div>
    );
};

export default MainLayout;
